import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  ErrorDisplayUnsavedItems,
  ProducePreviewOverlapLoadingOverlay,
  ProductionChainsStatusBar,
  ProductionChainsStepPreview,
  ProductionNoResult,
  ProductionPreviewChainDetails,
  ProductionPreviewDetails,
} from "@ryde/controls";
import { useBookingsBulkActions, useConfirmDirtyField } from "@ryde/redux";
import {
  APP_VERSION,
  IInjectedDrawerProps,
  IProduceJoinDto,
  IProductionChainPreviewStep,
  IProductionPreviewProps,
  isProductionUiFirstSelector,
  parseBoolean,
  productionPollGroupStateAtom,
  ProductionTypeEnum,
  productionUiChainSelector,
  resolveProduceDirectionsServicePayload,
} from "@ryde/shared";

import { CommonDefaultDrawer } from "../common-default-drawer";
import { BaseDrawer, FormFooter } from "../drawer";
import {
  ProductionClearCacheButton,
  ProductionViewLogsButton,
} from "../rich-logs";
import { DrawerProduceWrapperStyled } from "./production-preview.bookings.styled";
import { useProductionPreviewDrawer } from "./use-production-preview-drawer";

export const ProductionPreview: FC<
  IProductionPreviewProps & IInjectedDrawerProps
> = ({
  onCloseDrawer,
  action,
  productionType,
  rideTypeId,
  criteria,
  summaryItems,
  resolveFn,
  isCloseAfterProductionFinish,
  isRedirectToProducedRidesAfterProductionFinish = true,
  drawerNumber,
  chain,
  filterCourseIds,
  filterCourseSchedules,
  directionsService,
  sendToSupplierAfterProduce,
  doNotLockBookings,
  assignDocksDuringProduction,
}) => {
  const { t } = useTranslation();
  const setProductionGroupState = useSetRecoilState(
    productionPollGroupStateAtom,
  );
  const isProductionUiFirst = useRecoilValue(isProductionUiFirstSelector);
  const productionUiChain = useRecoilValue(productionUiChainSelector);
  const [previewChainSteps, setPreviewChainSteps] = useState<
    IProductionChainPreviewStep[]
  >([]);
  const { cleanupPreviousProductionData } = useBookingsBulkActions();
  const [, startTransition] = useTransition();
  const { applyIfConfirmed } = useConfirmDirtyField({
    customContent: <ErrorDisplayUnsavedItems unsavedItemText="" />,
  });
  const productionPayload: IProduceJoinDto = useMemo(
    () => ({
      action,
      productionType,
      criteria,
      rideTypeId,
      chain,
      filterCourseIds,
      filterCourseSchedules,
      ...resolveProduceDirectionsServicePayload(directionsService),
      sendToSupplierAfterProduce,
      doNotLockBookings: parseBoolean(doNotLockBookings),
      assignDocksDuringProduction,
      frontendVersion: APP_VERSION,
    }),
    [
      action,
      productionType,
      criteria,
      rideTypeId,
      chain,
      filterCourseIds,
      filterCourseSchedules,
      directionsService,
      sendToSupplierAfterProduce,
      doNotLockBookings,
      assignDocksDuringProduction,
    ],
  );
  const onBeforeClose = useCallback(() => {
    cleanupPreviousProductionData();
    setProductionGroupState(undefined);
  }, [cleanupPreviousProductionData, setProductionGroupState]);
  const handlePreviewLoaded = useCallback(
    (preview: { chains?: IProductionChainPreviewStep[] }) => {
      setPreviewChainSteps(preview.chains ?? []);
    },
    [],
  );
  const {
    productionId,
    isFinished,
    isStarted,
    isAborting,
    generalInfo,
    courseSchedulePreviews,
    hubBookingsPreview,
    hubBookingsPreviewSummary,
    hubAutoAssignedBookingIds,
    hubBookingsExcludedWithoutHubTypeIds,
    isPreviewLoading,
    shouldUseDrawerLoader,
    getOverlapLoadingCaption,
    groupedConflicts,
    conflicts,
    isOtherProductionInProgress,
    handleProductionAbort,
    handlePreviousProductionClick,
    loadProductionPreview,
    onCloseHandler,
    onSaveClick,
    saveLabel,
    cancelLabel,
    isSaveDisabled,
  } = useProductionPreviewDrawer({
    productionPayload,
    onCloseDrawer,
    resolveFn,
    action,
    isCloseAfterProductionFinish,
    isRedirectToProducedRidesAfterProductionFinish,
    onBeforeClose,
    onPreviewLoaded: handlePreviewLoaded,
  });
  const isNoBookings = generalInfo?.bookings?.length === 0;
  const isShuttleProductionType = Boolean(
    productionType?.includes(ProductionTypeEnum.Shuttle),
  );
  const isFlexProductionType = Boolean(
    productionType?.includes(ProductionTypeEnum.FlexCourse),
  );
  const isNoResultFound =
    isNoBookings && !isShuttleProductionType && !isFlexProductionType;
  const onCloseConfirmed = applyIfConfirmed({
    isDirty: Boolean(onSaveClick) && !isFinished,
  })((): void => {
    onCloseHandler();
  });
  useEffect(() => {
    startTransition(() => {
      loadProductionPreview();
    });
  }, [loadProductionPreview]);
  return (
    <BaseDrawer onCloseDrawer={onCloseConfirmed} drawerNumber={drawerNumber}>
      <CommonDefaultDrawer
        title={t("general.PRODUCTION_PREVIEW")}
        onCloseDrawer={onCloseConfirmed}
        Footer={
          !isNoResultFound ? (
            <FormFooter
              saveLabel={saveLabel}
              onSaveClick={onSaveClick}
              cancelLabel={cancelLabel}
              onCancelClick={onCloseHandler}
              isSaveDisabled={isSaveDisabled}
            >
              <ProductionViewLogsButton productionId={productionId} />
              <ProductionClearCacheButton />
            </FormFooter>
          ) : undefined
        }
      >
        <DrawerProduceWrapperStyled>
          {isPreviewLoading && shouldUseDrawerLoader && (
            <ProducePreviewOverlapLoadingOverlay
              caption={getOverlapLoadingCaption()}
            />
          )}
          {isNoResultFound && <ProductionNoResult onClose={onCloseHandler} />}
          {!isNoResultFound && (
            <>
              <ProductionChainsStatusBar />
              {isProductionUiFirst && (
                <ProductionPreviewDetails
                  generalInfo={generalInfo}
                  isStarted={isStarted}
                  productionPayload={productionPayload}
                  conflicts={conflicts}
                  loadProductionPreview={loadProductionPreview}
                  groupedConflicts={groupedConflicts}
                  handlePreviousProductionClick={handlePreviousProductionClick}
                  handleProductionAbort={handleProductionAbort}
                  isOtherProductionInProgress={isOtherProductionInProgress}
                  summaryItems={summaryItems}
                  isAborting={isAborting}
                  courseSchedulePreviews={courseSchedulePreviews}
                  hubBookingsPreview={hubBookingsPreview}
                  hubBookingsPreviewSummary={hubBookingsPreviewSummary}
                  hubAutoAssignedBookingIds={hubAutoAssignedBookingIds}
                  hubBookingsExcludedWithoutHubTypeIds={
                    hubBookingsExcludedWithoutHubTypeIds
                  }
                />
              )}
              {!isProductionUiFirst && !!productionUiChain && (
                <ProductionPreviewChainDetails
                  chain={productionUiChain}
                  isStarted={isStarted}
                  isAborting={isAborting}
                  isOtherProductionInProgress={isOtherProductionInProgress}
                  handleProductionAbort={handleProductionAbort}
                  handlePreviousProductionClick={handlePreviousProductionClick}
                />
              )}
              {!isStarted && (
                <ProductionChainsStepPreview chainSteps={previewChainSteps} />
              )}
            </>
          )}
        </DrawerProduceWrapperStyled>
      </CommonDefaultDrawer>
    </BaseDrawer>
  );
};
